import React, { useCallback, useEffect, useRef, useState } from "react";
import * as htmlToImage from "html-to-image";
import "../styles/Root.css";
import Sidebar from "./sidebar";
import Canvas from "./canvas";
import Mainbar from "./Mainbar";
import shortid from "shortid";
import {
  isHtmlTag,
  splitElementKey,
  generateElementKey,
  cleanElementName,
  getFormattedDate,
  getElementName,
  generateRootId,
  stringifyClassNames,
} from "../lib/helpers";
import { trackEvent } from "../lib/tracking";
import { useDispatch, useSelector } from "react-redux";
import {
  incrementExportId,
  incrementTotalExports,
  setGlobalProperties,
  setLocalProperties,
  setNotification,
  setDomOutline,
} from "../services/store";
import { message, Progress } from "antd";
import _ from "lodash";
import { ELEMENT_KEYS } from "../lib/constants";

const getFileName = ({ filename, exportId, idx, totalPages }) => {
  const filenameTemplate = [
    ["#", exportId],
    totalPages > 1 ? [".", idx + 1] : [],
    [" [", getFormattedDate(), "] "],
    [filename || "export", ".png"],
  ];
  return filenameTemplate.map((part) => part.join("")).join("");
};

const Root = () => {
  const [selectedElement, setSelectedElement] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const dispatch = useDispatch();
  const {
    data,
    filename,
    propertyType,
    pages,
    globalProperties,
    localProperties,
    exportId,
    notification,
    activePlatform,
  } = useSelector((state) => state.grid);

  const isGlobal = propertyType === "global";
  const postRef = useRef({});
  const canvasContainerRef = useRef();

  useEffect(() => {
    canvasContainerRef.current.addEventListener(
      "click",
      (e) => {
        const tag = _.toLower(e.target.tagName);
        const elemObj = getElementName(tag, e.target.classList);
        const pageId = e.target.closest("[data-page-id]")?.dataset?.pageId;

        // console.log("tag, elemObj::-", tag, elemObj);
        if (elemObj) {
          // console.log("Element recognized, updating selection", elemObj);

          e.stopPropagation();
          e.preventDefault();
          let target = e.target;

          if (elemObj.match === "custom") {
            // ex., the table element cannot be detected directly as the user might click on td, tr etc. So we need to find the closest table element and select that.
            target = e.target.closest(elemObj.elementName);
          }

          const isRootEditor = [...e.target.classList].includes(
            ELEMENT_KEYS.RAW_ROOT_EDITOR,
          );

          let id = isRootEditor ? null : target.dataset.id;
          if (!id && !isRootEditor) {
            id = shortid();
            target.dataset.id = id;
          }

          const elementKey = generateElementKey(
            pageId,
            elemObj.elementName,
            id,
          );
          _updateSelectedElement(elementKey);
          repaintHtmlElements(elementKey, "outlined", "add");
        } else {
          // If the clicked element is not recognized, clear the selection
          // console.log("Element not recognized, clearing selection");
          _updateSelectedElement("");
        }
      },
      true,
    );
  }, [canvasContainerRef.current]);

  useEffect(() => {
    // refer to the entire canvas. all the pages are inside the canvas.
    const canvas = canvasContainerRef.current;
    // apply global properties to all the html tags in the canvas on load
    for (const element in globalProperties) {
      if (isHtmlTag(element)) {
        canvas.querySelectorAll(element).forEach((el) => {
          const elementKey = generateElementKey(null, element);
          const classes = Object.values(globalProperties[element]);
          repaintHtmlElements(elementKey, classes, "set");
        });
      }
    }
  }, [globalProperties, data, canvasContainerRef.current]);

  useEffect(() => {
    if (!notification) return;
    setTimeout(() => {
      showMsg(notification);
    }, 500);
  }, [notification]);

  useEffect(() => {
    // Generate outline
    const ref = postRef.current[selectedElement];

    if (data && ref) {
      const processNode = (node, result = []) => {
        if (!node) return result;

        node.childNodes.forEach((el) => {
          const tag = _.toLower(el.tagName);

          const element = getElementName(tag, el.classList) || {
            elementName: "-",
          };
          result.push({
            element,
            children: processNode(el),
          });
        });

        return result;
      };

      const canvasOutline = processNode(ref, []);
      dispatch(
        setDomOutline([
          {
            element: {
              elementName: `.${ELEMENT_KEYS.RAW_ROOT_EDITOR}`,
            },
            children: canvasOutline,
          },
        ]),
      );
    }
  }, [data, postRef.current, selectedElement, pages]);

  // Function to update the selected element and manage outlined class
  const _updateSelectedElement = (newElement) => {
    setSelectedElement((prevElement) => {
      const { element } = splitElementKey(prevElement);

      // If the previously selected element is an HTML tag or a custom parsing element, remove the "outlined" class from it
      const prevElementObj = getElementName(element);
      if (prevElementObj)
        repaintHtmlElements(prevElement, "outlined", "remove");

      return newElement;
    });
  };

  const handleDownload = useCallback(() => {
    _updateSelectedElement("");
    setProgressPercent(20);
    const eachProgress = Math.floor(80 / pages.length);
    pages.forEach((page, idx) => {
      const refId = generateRootId(idx);

      const imageConfig = {
        cacheBust: true,
        quality: 1,
        width: page.containerWidth,
        height: page.containerHeight,
      };

      htmlToImage
        .toPng(postRef.current[refId], imageConfig)
        .then((dataUrl) => {
          dispatch(incrementTotalExports());
          const link = document.createElement("a");

          link.download = getFileName({
            filename,
            exportId,
            idx,
            totalPages: pages.length,
          });
          link.href = dataUrl;
          link.click();
          setProgressPercent((prev) => {
            const newPercent = prev + eachProgress;
            if (newPercent >= 100) {
              dispatch(setNotification("Export completed"));

              return 100;
            }
            return newPercent;
          });
        })
        .catch((err) => {
          console.error(err);
        });
    });
    setTimeout(() => {
      dispatch(incrementExportId());
      setProgressPercent(0);
      trackEvent("Exported Files", {
        totalFiles: pages.length,
        activePlatform,
      });
    }, 3000);
  }, [postRef, pages, filename, exportId]);

  const showMsg = (msg) => {
    messageApi.info(msg);
    setTimeout(() => {
      dispatch(setNotification(null));
    }, 100);
  };

  const repaintHtmlElements = (targetElement, classList, action = "set") => {
    if (!targetElement) return;

    const { element, uid } = splitElementKey(targetElement);

    // If its not a global property, then we need to target the specific element with the data-id. When clicking on a element, this function is called with action "add" to add the outlined class. In this case, we need to target the specific element.
    const selector =
      (uid && !isGlobal) || action === "add"
        ? `${element}[data-id='${uid}']`
        : element;

    classList = [].concat(classList);
    if (element === splitElementKey(selectedElement).element) {
      classList.push("outlined");
    }

    const elements = canvasContainerRef.current.querySelectorAll(selector);
    const finalClasses = stringifyClassNames(undefined, classList);

    elements.forEach((el) => {
      if (action === "remove") {
        el.classList.remove(...finalClasses.split(" "));
      } else if (action === "set") {
        el.classList = finalClasses;
      } else if (action === "add") {
        el.classList.add(...finalClasses.split(" "));
      }
    });
  };

  const handlePropertyChange = (property, value) => {
    const { element } = splitElementKey(selectedElement);
    let finalProperties;

    const elementClean = cleanElementName(element);

    if (isGlobal) {
      finalProperties = {
        ...(globalProperties[elementClean] || {}),
        [property]: value,
      };

      dispatch(
        setGlobalProperties({
          ...globalProperties,
          [elementClean]: finalProperties,
        }),
      );

      // Final properties is global -> local
      finalProperties = {
        ...finalProperties,
        ...localProperties[selectedElement],
      };
    } else {
      finalProperties = {
        ...(localProperties[selectedElement] || {}),
        [property]: value,
      };

      dispatch(
        setLocalProperties({
          ...localProperties,
          [selectedElement]: finalProperties,
        }),
      );

      // Final properties is global -> local
      finalProperties = {
        ...globalProperties[elementClean],
        ...finalProperties,
      };
    }
  };

  const canvasProps = {
    canvasContainerRef,
    postRef,
    selectedElement,
    _updateSelectedElement,
  };

  const mainbarProps = {
    handleDownload,
  };

  const sidebarProps = {
    selectedElement,
    handlePropertyChange,
    isGlobal,
  };

  return (
    <div className="flex items-center flex-col p-0 h-full">
      {!!progressPercent && (
        <Progress
          percent={progressPercent}
          strokeLinecap="butt"
          status="active"
          showInfo={false}
        />
      )}
      <div className="flex items-start gap-0 w-full grow overflow-hidden">
        <Mainbar {...mainbarProps} />
        <Canvas {...canvasProps} />
        <Sidebar {...sidebarProps} />
      </div>
      {contextHolder}
    </div>
  );
};

export default Root;
